import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { DataTable, Primary, Td, Th, Thead, Tr } from "@/components/steel/table"
import { Meter, PageHeader, Section, StatusBadge, ViewToggle } from "@/components/steel"
import { ProjectsKanban, type KanbanProject } from "@/components/projects-kanban"
import { formatCurrency, shortMoney } from "@/lib/utils"
import { stageLabel, stageOpacity } from "@/lib/projects"

type ProjectRow = KanbanProject & {
  client: string
  location: string
}

export default function ProjectsIndex({ projects }: { projects: ProjectRow[] }) {
  const [view, setView] = useState<"table" | "board">("table")

  const active = projects.filter((p) => p.stage !== "complete").length
  const contracted = projects.reduce((sum, p) => sum + p.totalRevenue, 0)

  return (
    <>
      <Head title="Projects" />

      <PageHeader
        title="Projects"
        meta={`${active} active · ${shortMoney(contracted)} contracted`}
        actions={
          <ViewToggle
            value={view}
            onChange={setView}
            options={[
              { value: "table", label: "Table" },
              { value: "board", label: "Board" },
            ]}
          />
        }
      />

      {view === "table" ? (
        <Section className="pb-7 pt-3.5">
          <DataTable>
            <Thead>
              <Th>Project</Th>
              <Th>Stage</Th>
              <Th className="w-[170px]">Budget used</Th>
              <Th align="right">Budget</Th>
              <Th align="right">Spend</Th>
              <Th align="right">Contract</Th>
              <Th align="right">Crew</Th>
              <Th align="right">Status</Th>
            </Thead>
            <tbody>
              {projects.map((p) => {
                const pct = p.initialBudget > 0 ? (p.currentSpend / p.initialBudget) * 100 : 0
                return (
                  <Tr key={p.id} onClick={() => router.visit(`/projects/${p.id}`)}>
                    <Td>
                      <Primary title={p.name} sub={[p.id, p.client, p.location].filter(Boolean).join(" · ")} />
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="h-[5px] w-[5px] rounded-full bg-foreground"
                          style={{ opacity: stageOpacity(p.stage) }}
                        />
                        {stageLabel(p.stage)}
                      </span>
                    </Td>
                    <Td>
                      <Meter pct={pct} label={`${Math.round(pct)}%`} />
                    </Td>
                    <Td align="right" num muted>
                      {formatCurrency(p.initialBudget)}
                    </Td>
                    <Td align="right" num>
                      {formatCurrency(p.currentSpend)}
                    </Td>
                    <Td align="right" num accent>
                      {formatCurrency(p.totalRevenue)}
                    </Td>
                    <Td align="right" num muted>
                      {p.teamSize}
                    </Td>
                    <Td align="right">
                      <StatusBadge status={p.status} />
                    </Td>
                  </Tr>
                )
              })}
            </tbody>
          </DataTable>
        </Section>
      ) : (
        <ProjectsKanban projects={projects} />
      )}
    </>
  )
}
